import React, { useState, useEffect } from "react";
import axios from "../axios";
import StoreAutocomplete from "./StoreAutocomplete";
import { logger } from "../services/logger";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { toast } from "react-toastify";

/**
 * CommunityProductForm — formulaire rapide pour sauvegarder un produit inconnu
 * dans la liste personnelle du client.
 *
 * Props :
 *   barcode        — code-barres pré-rempli (string)
 *   userId         — identifiant de l'utilisateur connecté
 *   geolocEnabled  — boolean, indique si la géolocalisation est activée
 *   onSuccess(scan) — callback avec le scan créé
 *   onCancel()      — annuler et fermer le formulaire
 */

const CATEGORIES = [
  { value: "alimentaire", label: "🍎 Alimentaire", fieldLabel: "Contenance / Poids", placeholder: "ex: 500g, 1L" },
  { value: "beaute", label: "💄 Beauté / Hygiène", fieldLabel: "Contenance", placeholder: "ex: 200ml, 50g" },
  { value: "vetements", label: "👕 Vêtements / Mode", fieldLabel: "Taille", placeholder: "ex: M, 42, XL" },
  { value: "electronique", label: "📱 Électronique", fieldLabel: "Modèle", placeholder: "ex: iPhone 15, Galaxy S22" },
  { value: "maison", label: "🏠 Maison / Déco", fieldLabel: "Dimensions", placeholder: "ex: 40x60cm" },
  { value: "autre", label: "📦 Autre", fieldLabel: "Informations supplémentaires", placeholder: "Décrivez le produit..." },
];

const CommunityProductForm = ({ barcode, userId, geolocEnabled, onSuccess, onCancel }) => {
  const [name, setName]             = useState("");
  const [category, setCategory]     = useState("");
  const [specific, setSpecific]     = useState("");
  const [store, setStore]           = useState("");
  const [note, setNote]             = useState("");
  const [price, setPrice]           = useState("");
  const [photo, setPhoto]           = useState(null);
  const [photoLabel, setPhotoLabel] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  const [location, setLocation]     = useState(null);
  const [geoStatus, setGeoStatus]   = useState("idle"); // idle | loading | done | failed
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  // Récupère la géolocalisation au montage si activée
  useEffect(() => {
    if (!geolocEnabled) return;
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus("done");
      },
      () => setGeoStatus("failed"),
      { timeout: 6000 }
    );
  }, [geolocEnabled]);

  // Réinitialise le champ spécifique quand la catégorie change
  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setSpecific("");
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError(null);
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError("La photo ne doit pas dépasser 5 Mo.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhoto(ev.target.result);
      setPhotoLabel(file.name);
    };
    reader.onerror = () => {
      setPhotoError("Impossible de lire cette image. Réessaie avec un autre fichier.");
    };
    try {
      reader.readAsDataURL(file);
    } catch {
      setPhotoError("Erreur lors de l'accès à la photo. Réessaie ou choisis une image depuis la galerie.");
    }
  };

  const compressImage = (dataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const maxWidth = 600;
      const ratio = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.5));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

  const takePhoto = async () => {
    try {
      const perms = await Camera.checkPermissions();
      if (perms.camera !== "granted") {
        const req = await Camera.requestPermissions({ permissions: ["camera"] });
        if (req.camera !== "granted") {
          toast.error("Permission caméra refusée. Autorise l'accès dans les paramètres.");
          return;
        }
      }

      // Laisse 500ms au stream précédent (scanner Html5Qrcode) pour se libérer
      await new Promise(resolve => setTimeout(resolve, 500));

      const img = await Camera.getPhoto({
        quality: 50,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: 600,
      });
      setPhoto(img.dataUrl);
      setPhotoLabel("Photo prise");
      setPhotoError(null);
    } catch (err) {
      const msg = err?.message || String(err);
      console.error("[takePhoto] Camera error:", msg, "| code:", err?.code);
      if (!msg.toLowerCase().includes("cancel")) {
        setPhotoError("Impossible d'accéder à la caméra. Essaie depuis la galerie.");
        toast.error(`Erreur caméra : ${msg.slice(0, 80)}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !category) return;
    setSubmitting(true);
    setError(null);
    try {
      const photoToSend = photo ? await compressImage(photo) : null;
      const res = await axios.post("/community-products", {
        barcode,
        name: name.trim(),
        addedBy: userId,
        store: store.trim() || null,
        location: location || null,
        photo: photoToSend || null,
        note: note.trim() || null,
        price: price ? parseFloat(price) : null,
        category,
        details: {
          category,
          specific: specific.trim() || null,
        },
      });
      onSuccess(res.data.scan);
    } catch (err) {
      logger.error('ProductCreation', 'Erreur création produit non référencé', {
        message: err?.message,
        status: err?.response?.status,
        responseData: err?.response?.data,
      });
      setError("Erreur lors de l'enregistrement. Réessaie.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: "8px",
    border: "1.5px solid #CBD5E1",
    fontSize: "0.875rem",
    background: "#F8FAFC",
    color: "#1e293b",
    boxSizing: "border-box",
    outline: "none",
    marginBottom: "10px",
    display: "block",
  };

  const activeCat = CATEGORIES.find((c) => c.value === category);

  return (
    <div style={{
      marginTop: "12px",
      padding: "14px 16px",
      background: "#F0FDF4",
      border: "1px solid #86EFAC",
      borderRadius: "10px",
    }}>
      <p style={{ fontSize: "0.78rem", fontWeight: 700, color: "#166534", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        📝 Ajouter ce produit à votre liste
      </p>

      {/* Géolocalisation */}
      {geolocEnabled && (
        <p style={{ fontSize: "0.75rem", color: "#475569", marginBottom: "10px" }}>
          {geoStatus === "loading" && "📍 Récupération de la position…"}
          {geoStatus === "done"    && "📍 Position acquise"}
          {geoStatus === "failed"  && "📍 Position non disponible"}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        {/* Barcode (lecture seule) */}
        <p style={{ fontSize: "0.72rem", fontFamily: "monospace", color: "#64748b", marginBottom: "8px" }}>
          🔢 {barcode}
        </p>

        {/* Nom — obligatoire */}
        <input
          style={inputStyle}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nom du produit *"
          required
          disabled={submitting}
        />

        {/* Catégorie — obligatoire */}
        <select
          style={{ ...inputStyle, cursor: "pointer" }}
          value={category}
          onChange={handleCategoryChange}
          required
          disabled={submitting}
        >
          <option value="">Catégorie *</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        {/* Champ spécifique selon catégorie */}
        {activeCat && (
          <input
            style={inputStyle}
            type="text"
            value={specific}
            onChange={(e) => setSpecific(e.target.value)}
            placeholder={`${activeCat.fieldLabel} (optionnel) — ${activeCat.placeholder}`}
            disabled={submitting}
          />
        )}

        {/* Magasin / lieu */}
        <StoreAutocomplete
          value={store}
          onChange={(v) => setStore(v)}
          lat={location?.lat}
          lng={location?.lng}
          disabled={submitting}
          inputStyle={inputStyle}
        />

        {/* Prix observé */}
        <input
          style={inputStyle}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Prix observé en € (optionnel)"
          disabled={submitting}
        />

        {/* Note personnelle */}
        <input
          style={inputStyle}
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note ou repère (optionnel)"
          disabled={submitting}
        />

        {/* Photo */}
        <div style={{ marginBottom: "12px" }}>
          <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: "6px" }}>📷 Photo (optionnel)</p>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {Capacitor.isNativePlatform() && (
              <button
                type="button"
                onClick={takePhoto}
                disabled={submitting}
                style={{
                  fontSize: "0.78rem", padding: "8px 12px", borderRadius: "8px",
                  border: "1px solid #e2e8f0", background: "#f8fafc",
                  color: "#475569", cursor: "pointer",
                }}
              >
                📷 Appareil photo
              </button>
            )}
            <label style={{
              fontSize: "0.78rem", padding: "8px 12px", borderRadius: "8px",
              border: "1px solid #e2e8f0", background: "#f8fafc",
              color: "#475569", cursor: "pointer", display: "inline-block",
            }}>
              🖼️ Galerie
              <input
                type="file"
                accept="image/*"
                onChange={handlePhoto}
                disabled={submitting}
                style={{ display: "none" }}
              />
            </label>
          </div>
          {photoLabel && !photoError && (
            <p style={{ fontSize: "0.72rem", color: "#16a34a", marginTop: "6px" }}>✅ {photoLabel}</p>
          )}
          {photoError && (
            <p style={{ fontSize: "0.72rem", color: "#dc2626", marginTop: "6px" }}>⚠️ {photoError}</p>
          )}
        </div>

        {error && (
          <p style={{ fontSize: "0.78rem", color: "#dc2626", marginBottom: "8px" }}>{error}</p>
        )}

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="submit"
            disabled={submitting || !name.trim() || !category}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: "8px",
              border: "none",
              background: submitting || !name.trim() || !category ? "#86EFAC" : "#16a34a",
              color: "white",
              fontWeight: 700,
              fontSize: "0.875rem",
              cursor: submitting || !name.trim() || !category ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "⏳ Enregistrement…" : "✅ Ajouter à ma liste"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              border: "none",
              background: "#F1F5F9",
              color: "#475569",
              fontWeight: 600,
              fontSize: "0.875rem",
              cursor: "pointer",
            }}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommunityProductForm;
